// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, cloneElement, Children, Fragment } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import ButtonGroup from '../ButtonGroup';
import Input from '../Input';
import Button from '../Button';
import IconButton from '../IconButton';
import Menu, { MenuItem, MenuSelectableItem } from '../Menu';

import './SearchBar.less';

/**
 * SearchBar component.
 */
class SearchBar extends Component {
    state = {
        focused: false,
        overMenu: false,
        value: '',
        highlightedValue: null,
    };

    componentWillReceiveProps() {
        this.setState({ highlightedValue: null });
    }

    isResponsive() {
        return this.props.responsive === null ? this.context.responsiveTabs || false : this.props.responsive;
    }

    getResults(innerResults = false) {
        const { results } = this.props;

        if (!results) {
            return [];
        }

        if (results.type === Menu || innerResults) {
            let res = [];
            const children = innerResults || results.props.children;
            for (let i = 0; i < children.length; i++) {
                const child = children[i];

                if (!child) {
                    continue;
                }

                if (child instanceof Array) {
                    res = res.concat(this.getResults(child));
                } else if (child.type === MenuItem || child.type === MenuSelectableItem) {
                    res.push(child.key);
                }
            }

            return res;
        }

        return results.map(item => item.value);
    }

    getHighlightedValue() {
        return this.state.highlightedValue || this.getResults()[0];
    }

    searchAction() {
        const { onItemSelect, results } = this.props;
        const highlightedValue = this.getHighlightedValue();

        if (onItemSelect && results) {
            const res = this.getResults();
            const currentIndex = res.indexOf(highlightedValue);

            if (currentIndex === -1) {
                this.handleSearch();
            } else {
                this.handleSelect(highlightedValue)();
            }
        } else {
            this.handleSearch();
        }
    }

    keyboardNavigation = key => {
        const { onItemSelect, results } = this.props;
        const highlightedValue = this.getHighlightedValue();

        if (!results || !onItemSelect) {
            return;
        }

        const res = this.getResults();
        const currentIndex = res.indexOf(highlightedValue);
        let nextIndex;

        if (key === 'ArrowUp') {
            nextIndex = Math.max(currentIndex - 1, 0);
        } else if (key === 'ArrowDown') {
            nextIndex = Math.min(currentIndex + 1, res.length - 1);
        }

        this.setState({ highlightedValue: currentIndex === -1 ? null : res[nextIndex] });
    };

    checkKeys(key) {
        if (key === 'Enter') {
            this.searchAction();
        } else if (key === 'Escape') {
            if (this.state.value) {
                this.handleReset();
            } else {
                this.handleBlur();
                this.handleMenuMouseOut();
            }
        } else if (key === 'ArrowUp' || key === 'ArrowDown') {
            this.keyboardNavigation(key);
        }
    }

    clearInput() {
        this.setState({ value: '' }, this.handleSearch);
    }

    handleFocus = () => {
        this.setState({ focused: true });
        this.search && this.search.focus();
    };

    handleBlur = () => {
        this.setState({ focused: false });
        this.search && this.search.blur();
    };

    handleMenuMouseOver = () => {
        this.setState({ overMenu: true });
    };

    handleMenuMouseOut = () => {
        this.setState({ overMenu: false });
    };

    handleKeyDown = e => {
        const { onTyping } = this.props;
        const { value } = e.target;

        if (['Enter', 'Escape', 'ArrowUp', 'ArrowDown'].indexOf(e.key) !== -1) {
            this.checkKeys(e.key);
            return;
        }

        this.setState({ value });

        if (onTyping) {
            onTyping(value, () => this.clearInput());
        }
    };

    handleSearch = () => {
        const { onSearch, onTyping } = this.props;
        const { value } = this.state;

        if (onSearch) {
            onSearch(value, () => this.clearInput());
        } else if (onTyping) {
            onTyping(value, () => this.clearInput());
        }
    };

    handleReset = () => {
        this.clearInput();
        this.handleFocus();
    };

    handleSelect = value => () => {
        const { onItemSelect } = this.props;
        this.setState({ highlightedValue: null });
        this.handleBlur();

        if (onItemSelect) {
            onItemSelect(value, () => this.clearInput());
        } else {
            this.clearInput();
        }
    };

    elementsIndexToKeyMapping(elements) {
        let result = [];
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];

            if (!element) {
                continue;
            }

            if (element instanceof Array) {
                result = result.concat(this.elementsIndexToKeyMapping(element));
            } else {
                result.push(element.key);
            }
        }

        return result;
    }

    renderResults() {
        const { baseClassName, results } = this.props;
        const { focused, overMenu } = this.state;
        const highlightedValue = this.getHighlightedValue();

        if (!results || results.length === 0) {
            return null;
        }

        return (
            <Menu
                className={classNames(
                    `${baseClassName}__results`,
                    {
                        [`${baseClassName}__results--hidden`]: !focused && !overMenu,
                    }
                )}
                onMouseOver={this.handleMenuMouseOver}
                onMouseOut={this.handleMenuMouseOut}
            >
                {results.map(item => (
                    <MenuItem
                        key={item.value}
                        active={item.value === highlightedValue}
                        onClick={this.handleSelect(item.value)}
                    >
                        {item.title}
                    </MenuItem>
                ))}
            </Menu>
        );
    }

    renderResultsMenu() {
        const { baseClassName, results, onItemSelect } = this.props;
        const { focused, overMenu } = this.state;
        const highlightedValue = this.getHighlightedValue();

        if (!results) {
            return null;
        }

        return cloneElement(results, {
            className: classNames(
                `${baseClassName}__results`,
                {
                    [`${baseClassName}__results--hidden`]: !focused && !overMenu,
                },
                results.className,
            ),
            onMouseOver: this.handleMenuMouseOver,
            onMouseOut: this.handleMenuMouseOut,
            onItemClick: (item, index) => {
                const indexMap = this.elementsIndexToKeyMapping(results.props.children);
                this.handleSelect(indexMap[index])();
            },
            children: Children.map(results.props.children, child => {
                if (onItemSelect && child && (child.type === MenuItem || child.type === MenuSelectableItem)) {
                    return cloneElement(child, { active: child.key === highlightedValue });
                }

                return child;
            }),
        });
    }

    render() {
        const {
            baseClassName,
            className,
            maxWidth,
            minWidth,
            leftAddon,
            loading,
            responsive,
            onSearch,
            onTyping,
            onItemSelect,
            results,
            inputProps,
            ...props
        } = this.props;
        const { focused, value } = this.state;
        const isCollapsed = this.isResponsive();

        return (
            <div
                className={classNames(
                    baseClassName,
                    {
                        [`${baseClassName}--focused`]: focused,
                    },
                    className,
                )}
                style={isCollapsed ? null : { maxWidth, minWidth }}
                {...props}
            >
                <ButtonGroup className={`${baseClassName}__group`}>
                    {leftAddon}

                    {!isCollapsed && (
                        <Fragment>
                            <Input
                                innerRef={ref => (this.search = ref)}
                                className={`${baseClassName}__input`}
                                size="fill"
                                value={value}
                                onChange={this.handleKeyDown}
                                onFocus={this.handleFocus}
                                onBlur={this.handleBlur}
                                onKeyDown={this.handleKeyDown}
                                {...inputProps}
                            />
                            {value && (
                                <IconButton
                                    className={`${baseClassName}__reset-button`}
                                    icon="remove"
                                    onClick={this.handleReset}
                                />
                            )}
                        </Fragment>
                    )}

                    <Button
                        className={`${baseClassName}__search-button`}
                        intent={focused ? 'primary' : null}
                        icon="search"
                        onClick={this.handleSearch}
                        state={loading ? 'loading' : null}
                    />
                </ButtonGroup>

                {(results && results.type === Menu) ? this.renderResultsMenu() : this.renderResults()}
            </div>
        );
    }
}

SearchBar.propTypes = {
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
    /**
     * Maximum width of SearchBar
     */
    maxWidth: PropTypes.number,
    /**
     * Minimum width of SearchBar
     */
    minWidth: PropTypes.number,
    /**
     * Addon in the left side or SearchBar.
     */
    leftAddon: PropTypes.element,
    /**
     * Should disable input and show spinner or not.
     */
    loading: PropTypes.bool,
    /**
     * Should collapse input or not. Used for control in independence of context.
     */
    responsive: PropTypes.bool,
    /**
     * Callback for pressing 'Enter' key or 'Search' button.
     *
     * arguments:
     * * value - search value
     * * clearHandler - function that clears input value
     */
    onSearch: PropTypes.func,
    /**
     * Callback for pressing any key in input. Used for a live search.
     *
     * arguments:
     * * value - search value
     * * clearHandler - function that clears input value
     */
    onTyping: PropTypes.func,
    /**
     * Callback for selecting menu item with mouse click or key pressing.
     *
     * arguments:
     * * value - selected value
     * * clearHandler - function that clears input value
     */
    onItemSelect: PropTypes.func,
    /**
     * Results to display.
     * Could be specified by array of objects with "value" and "title" properties or by Menu component.
     * If Menu component is used then key property of MenuItem will be treated as value.
     */
    results: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.shape({
            value: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
        })),
        PropTypes.element,
    ]),
    /**
     * Props of underlying input element.
     */
    inputProps: PropTypes.object,
};

SearchBar.defaultProps = {
    className: null,
    baseClassName: `${CLS_PREFIX}search-bar`,
    maxWidth: 500,
    minWidth: 270,
    leftAddon: null,
    loading: false,
    responsive: null,
    onSearch: null,
    onTyping: null,
    onItemSelect: null,
    results: null,
    inputProps: {},
};

SearchBar.contextTypes = {
    responsiveTabs: PropTypes.bool,
};

export default SearchBar;



// WEBPACK FOOTER //
// ./components/Tabs/SearchBar.js